import { useRef, useEffect } from 'react';
import type { Category } from '../types';
import { CATEGORIES } from '../data/questions';

interface SectionStepperProps {
  categories: Category[];
  currentCategory: Category;
  answeredByCategory: Record<Category, number>;
  totalByCategory: Record<Category, number>;
  onSectionClick: (category: Category) => void;
}

export default function SectionStepper({
  categories,
  currentCategory,
  answeredByCategory,
  totalByCategory,
  onSectionClick,
}: SectionStepperProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  const columnCount = Math.max(categories.length, 1);

  // Auto-scroll the active pill into view when section changes
  useEffect(() => {
    const activeEl = activeRef.current;
    const containerEl = scrollRef.current;
    if (!activeEl || !containerEl) return;

    const activeRect = activeEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();
    const isOutOfView =
      activeRect.left < containerRect.left || activeRect.right > containerRect.right;

    if (!isOutOfView) return;

    activeEl.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }, [currentCategory]);

  return (
    <div className="w-full">
      {/* Pill row */}
      <div
        ref={scrollRef}
        className="grid gap-1.5 py-2 sm:flex sm:gap-2 sm:overflow-visible sm:justify-center sm:px-1"
        style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
      >
        {categories.map((cat) => {
          const meta = CATEGORIES.find((c) => c.id === cat);
          if (!meta) return null;

          const answered = answeredByCategory[cat] || 0;
          const total = totalByCategory[cat] || 0;
          const isComplete = total > 0 && answered === total;
          const isCurrent = cat === currentCategory;
          const unansweredCount = total - answered;

          return (
            <button
              key={cat}
              ref={isCurrent ? activeRef : undefined}
              onClick={() => onSectionClick(cat)}
              className={`
                relative mx-auto w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-base sm:text-lg sm:flex-shrink-0
                transition-all duration-200
                ${
                  isCurrent
                    ? 'ring-2 ring-primary-500 bg-primary-500/15 scale-100 sm:scale-110'
                    : isComplete
                      ? 'bg-green-500/15 ring-1 ring-green-500/40'
                      : 'bg-white/5 hover:bg-white/10'
                }
              `}
              aria-label={`${meta.label}${isComplete ? ' (complete)' : unansweredCount > 0 ? ` (${unansweredCount} unanswered)` : ''}`}
            >
              {meta.emoji}
              {isComplete && !isCurrent && (
                <span className="absolute -top-0.5 -right-0.5 w-3 h-3 sm:w-3.5 sm:h-3.5 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-1.5 h-1.5 sm:w-2 sm:h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
              )}
              {!isComplete && unansweredCount > 0 && answered > 0 && !isCurrent && (
                <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-amber-500 text-white text-[8px] sm:text-[9px] font-bold rounded-full flex items-center justify-center">
                  {unansweredCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

    </div>
  );
}
